import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { tokens } from "../../design-system/tokens";
import Menu3DotsButton from "../icons/Menu3DotsButton";
import Button from "./Button";
import styles from "./Dropdown.module.css";

const VIEWPORT_MARGIN = 8;
const ANCHOR_GAP = 8;

export type DropdownItem = {
  id: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
};

export type DropdownProps = {
  items: DropdownItem[];
  /** Text trigger; omit to use the ⋮ menu button. */
  triggerLabel?: string;
  triggerColor?: "secondary" | "outlineSecondary" | "outlineAccent" | "primary";
  triggerSize?: "small" | "medium" | "large";
  ariaLabel?: string;
  className?: string;
  disabled?: boolean;
};

const menuPanelStyle: React.CSSProperties = {
  backgroundColor: tokens.colors.background,
  boxShadow: tokens.shadows.md,
  borderRadius: tokens.borderRadius.xl,
  border: `1px solid ${tokens.colors.border}`,
  ["--dropdown-menu-item-hover-bg" as string]:
    tokens.buttonColors.secondaryLightHover,
};

const menuItemStyle: React.CSSProperties = {
  ...tokens.typography.menuItem,
  color: tokens.colors.text.primary,
  borderRadius: tokens.borderRadius.md,
};

export default function Dropdown({
  items,
  triggerLabel,
  triggerColor = "outlineSecondary",
  triggerSize = "medium",
  ariaLabel = "תפריט פעולות",
  className,
  disabled = false,
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(
    null
  );
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggle = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
  };

  const close = () => setOpen(false);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !menuRef.current) {
      setPosition(null);
      return;
    }

    const anchor = triggerRef.current.getBoundingClientRect();
    const menu = menuRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top = anchor.bottom + ANCHOR_GAP;
    if (top + menu.height > vh - VIEWPORT_MARGIN) {
      top = anchor.top - menu.height - ANCHOR_GAP;
    }
    top = Math.max(
      VIEWPORT_MARGIN,
      Math.min(top, vh - menu.height - VIEWPORT_MARGIN)
    );

    let left = anchor.right - menu.width;
    left = Math.max(
      VIEWPORT_MARGIN,
      Math.min(left, vw - menu.width - VIEWPORT_MARGIN)
    );

    setPosition({ top, left });
  }, [open, items.length]);

  useEffect(() => {
    if (!open) return;

    const isInside = (node: Node | null) => {
      if (!node) return false;
      return (
        !!triggerRef.current?.contains(node) ||
        !!menuRef.current?.contains(node)
      );
    };

    const onPointer = (event: MouseEvent | TouchEvent) => {
      if (!isInside(event.target as Node)) close();
    };

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };

    const onScrollOrResize = () => close();

    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer, { passive: true });
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);

    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open]);

  const floatingStyle: React.CSSProperties =
    open && position
      ? { top: position.top, left: position.left, visibility: "visible" }
      : { top: -9999, left: -9999, visibility: "hidden" };

  const enabledItems = items.filter((item) => !item.disabled);

  return (
    <div
      ref={triggerRef}
      className={[styles.root, className].filter(Boolean).join(" ")}
      aria-expanded={open}
      aria-haspopup="menu"
    >
      {triggerLabel ? (
        <Button
          label={triggerLabel}
          color={triggerColor}
          size={triggerSize}
          width="auto"
          disabled={disabled}
          onClick={toggle}
        />
      ) : (
        <Menu3DotsButton
          color="light"
          buttonSize="medium"
          disabled={disabled}
          aria-label={ariaLabel}
          aria-expanded={open}
          onClick={toggle}
        />
      )}
      {createPortal(
        <AnimatePresence>
          {open ? (
            <motion.div
              ref={menuRef}
              role="menu"
              className={styles.menuContent}
              style={{ ...menuPanelStyle, ...floatingStyle }}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
            >
              {items.map((item) => (
                <motion.div
                  key={item.id}
                  role="menuitem"
                  className={
                    item.disabled
                      ? `${styles.menuItem} ${styles.menuItemDisabled}`
                      : styles.menuItem
                  }
                  style={{ ...menuItemStyle, userSelect: "none" }}
                  onClick={() => {
                    if (item.disabled) return;
                    close();
                    item.onClick();
                  }}
                >
                  {item.label}
                </motion.div>
              ))}
              {enabledItems.length === 0 ? (
                <div
                  className={styles.menuItemDisabled}
                  style={{ ...menuItemStyle, padding: "8px 10px" }}
                >
                  אין פעולות זמינות
                </div>
              ) : null}
            </motion.div>
          ) : null}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}
